import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Portal,
  Progress,
  Stack,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { UseDataUserContext } from "./useDataUserAuthenticated";
import EnvioInformeProgressModal from "../../Components/Common/EnvioInformeProgressModal";
import EnvioInformeResultModal from "../../Components/Common/EnvioInformeResultModal";

const STORAGE_KEY = "wam.envioInforme.jobs";
const POLL_TIMEOUT_MS = 180000;
const POLL_BASE_DELAY_MS = 2000;
const POLL_MAX_DELAY_MS = 8000;
const TIMEOUT_TOAST_ID = "envio-informe-timeout-global";

const buildInitialStatus = (total = 0) => ({
  state: "PENDING",
  progress: {
    stage: "Preparando envío...",
    percent: null,
    done: 0,
    total,
  },
  result: null,
  error: null,
});

const isActiveState = (state) => state === "PENDING" || state === "PROGRESS";
const isTerminalState = (state) =>
  state === "SUCCESS" || state === "FAILURE" || state === "PARTIAL_SUCCESS";

const EnvioInformeJobContext = createContext({
  startJob: () => {},
});

export const useEnvioInformeJob = () => useContext(EnvioInformeJobContext);

const serializeJobs = (jobs, order) => {
  const snapshot = order
    .map((id) => jobs[id])
    .filter(Boolean)
    .map((job) => ({
      jobId: job.jobId,
      meta: job.meta,
      status: job.status,
      startedAt: job.startedAt,
      completed: job.completed,
      dismissedProgress: job.dismissedProgress,
      retryInfo: job.retryInfo,
    }));
  return JSON.stringify({ jobs: snapshot, order });
};

const deserializeJobs = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.jobs)) return null;
    const entries = {};
    parsed.jobs.forEach((job) => {
      entries[job.jobId] = {
        jobId: job.jobId,
        meta: job.meta || {},
        status: job.status || buildInitialStatus(),
        startedAt: job.startedAt || Date.now(),
        completed: Boolean(job.completed),
        dismissedProgress: Boolean(job.dismissedProgress),
        retryInfo: job.retryInfo || { canRetry: false },
      };
    });
    const order = Array.isArray(parsed.order)
      ? parsed.order.filter((id) => entries[id])
      : Object.keys(entries);
    return { entries, order };
  } catch (err) {
    console.warn("[EnvioInformeJobProvider] No se pudo leer STORAGE_KEY:", err);
    return null;
  }
};

export const EnvioInformeJobProvider = ({ children }) => {
  const { useInfoUserAuth } = useContext(UseDataUserContext);
  const token = useInfoUserAuth?.data?.token || "";
  const toast = useToast();

  const [jobs, setJobs] = useState({});
  const [jobOrder, setJobOrder] = useState([]);
  const [activeModalJobId, setActiveModalJobId] = useState(null);
  const [activeModalType, setActiveModalType] = useState(null);
  const [jobCenterOpen, setJobCenterOpen] = useState(false);

  const jobsRef = useRef({});
  const pollMetaRef = useRef({});
  const pollTimeoutRef = useRef({});

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const persistJobs = useCallback(
    (nextJobs, nextOrder) => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          serializeJobs(nextJobs, nextOrder)
        );
      } catch {
        /* noop */
      }
    },
    []
  );

  useEffect(() => {
    persistJobs(jobs, jobOrder);
  }, [jobs, jobOrder, persistJobs]);

  const stopPolling = useCallback((jobId) => {
    if (pollTimeoutRef.current[jobId]) {
      clearTimeout(pollTimeoutRef.current[jobId]);
      delete pollTimeoutRef.current[jobId];
    }
    delete pollMetaRef.current[jobId];
  }, []);

  const handlePollingTimeout = useCallback(
    (jobId) => {
      stopPolling(jobId);
      setJobs((prev) => {
        const job = prev[jobId];
        if (!job) return prev;
        return {
          ...prev,
          [jobId]: {
            ...job,
            dismissedProgress: true,
            retryInfo: { canRetry: true },
          },
        };
      });
      if (!toast.isActive(TIMEOUT_TOAST_ID)) {
        toast({
          id: TIMEOUT_TOAST_ID,
          status: "warning",
          title: "El envío sigue en proceso",
          description: "No pudimos consultar el estado. Podés reintentarlo.",
        });
      }
    },
    [stopPolling, toast]
  );

  const openProgressModal = useCallback((jobId) => {
    const job = jobsRef.current[jobId];
    if (!job) return;
    setJobs((prev) => {
      const current = prev[jobId];
      if (!current) return prev;
      return { ...prev, [jobId]: { ...current, dismissedProgress: false } };
    });
    setActiveModalJobId(jobId);
    setActiveModalType("progress");
  }, []);

  const openResultModal = useCallback((jobId) => {
    if (!jobsRef.current[jobId]) return;
    setActiveModalJobId(jobId);
    setActiveModalType("result");
  }, []);

  const closeModals = useCallback(() => {
    setActiveModalJobId(null);
    setActiveModalType(null);
  }, []);

  const closeProgressModal = useCallback(() => {
    if (activeModalJobId) {
      setJobs((prev) => {
        const job = prev[activeModalJobId];
        if (!job) return prev;
        return {
          ...prev,
          [activeModalJobId]: { ...job, dismissedProgress: true },
        };
      });
    }
    closeModals();
  }, [activeModalJobId, closeModals]);

  const closeResultModal = useCallback(() => {
    closeModals();
  }, [closeModals]);

  const showResultToast = useCallback(
    ({ title, description, jobId }) => {
      toast({
        duration: 10000,
        position: "bottom-right",
        render: ({ onClose }) => (
          <Box
            bg="gray.800"
            color="white"
            p={4}
            borderRadius="lg"
            boxShadow="lg"
            maxW="sm"
          >
            <Text fontWeight="semibold">{title}</Text>
            <Text fontSize="sm" color="gray.200" mt={1}>
              {description}
            </Text>
            <Button
              size="sm"
              colorScheme="blue"
              mt={3}
              onClick={() => {
                openResultModal(jobId);
                onClose();
              }}
            >
              Ver detalle
            </Button>
          </Box>
        ),
      });
    },
    [openResultModal, toast]
  );

  const finalizeJob = useCallback(
    (jobId, payload) => {
      stopPolling(jobId);
      setJobs((prev) => {
        const job = prev[jobId];
        if (!job) return prev;
        return {
          ...prev,
          [jobId]: {
            ...job,
            completed: true,
            status: {
              ...job.status,
              state: payload.state,
              progress: {
                ...(job.status?.progress || {}),
                stage: "Procesamiento finalizado",
                percent: 100,
              },
              result: payload.result || job.status?.result || null,
              error: payload.error || job.status?.error || null,
            },
          },
        };
      });

      const ok = payload.result?.sent_count ?? payload.result?.ok_total ?? 0;
      const fail = payload.result?.error_count ?? payload.result?.fail_total ?? 0;
      const summary = payload.result?.status_summary;
      let toastTitle = "Envío completado";
      if (payload.state === "FAILURE" || summary === "failed" || ok === 0) {
        toastTitle = "Envío fallido";
      } else if (payload.state === "PARTIAL_SUCCESS" || summary === "partial" || fail > 0) {
        toastTitle = "Envío parcial";
      }
      const desc =
        payload.error?.message ||
        (ok || fail
          ? `${ok} OK${fail ? `, ${fail} fallidos` : ""}`
          : "Sin resultados.");

      const hasModalOpen = Boolean(activeModalJobId);
      const jobsCount = Object.keys(jobsRef.current).length;
      if (!hasModalOpen && jobsCount <= 1) {
        openResultModal(jobId);
      } else {
        showResultToast({ title: toastTitle, description: desc, jobId });
      }
    },
    [activeModalJobId, openResultModal, showResultToast, stopPolling]
  );

  const updateJobStatus = useCallback((jobId, updater) => {
    setJobs((prev) => {
      const job = prev[jobId];
      if (!job) return prev;
      const nextStatus =
        typeof updater === "function" ? updater(job.status) : updater;
      return { ...prev, [jobId]: { ...job, status: nextStatus } };
    });
  }, []);

  const fetchStatus = useCallback(
    async (jobId) => {
      if (!jobId || !token) return;
      if (!jobsRef.current[jobId]) return;
      try {
        const res = await axios.get(
          "/reportes/enviar-informe-propietarios/status/",
          {
            params: { job_id: jobId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = res.data || {};
        const progress = data.progress || {};
        updateJobStatus(jobId, (prevStatus) => ({
          state: data.state || prevStatus?.state || "PENDING",
          progress: {
            stage: progress.stage || prevStatus?.progress?.stage || "Procesando envío...",
            percent:
              typeof progress.percent === "number"
                ? progress.percent
                : prevStatus?.progress?.percent ?? null,
            done: progress.done ?? prevStatus?.progress?.done ?? 0,
            total: progress.total ?? prevStatus?.progress?.total ?? 0,
          },
          result: data.result || prevStatus?.result || null,
          error: data.error || prevStatus?.error || null,
        }));

        if (isTerminalState(data.state)) {
          finalizeJob(jobId, data);
          return;
        }

        const pollMeta = pollMetaRef.current[jobId];
        if (!pollMeta) return;
        const elapsed = Date.now() - pollMeta.start;
        if (elapsed >= POLL_TIMEOUT_MS) {
          handlePollingTimeout(jobId);
          return;
        }
        pollMeta.delay = Math.min(
          pollMeta.delay + 1000,
          POLL_MAX_DELAY_MS
        );
        pollMeta.attempts += 1;
        pollTimeoutRef.current[jobId] = setTimeout(
          () => fetchStatus(jobId),
          pollMeta.delay
        );
      } catch (error) {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          stopPolling(jobId);
          return;
        }
        const pollMeta = pollMetaRef.current[jobId];
        if (!pollMeta) return;
        const elapsed = Date.now() - pollMeta.start;
        if (elapsed >= POLL_TIMEOUT_MS) {
          handlePollingTimeout(jobId);
          return;
        }
        pollMeta.delay = Math.min(
          pollMeta.delay + 1000,
          POLL_MAX_DELAY_MS
        );
        pollMeta.attempts += 1;
        pollTimeoutRef.current[jobId] = setTimeout(
          () => fetchStatus(jobId),
          pollMeta.delay
        );
      }
    },
    [finalizeJob, handlePollingTimeout, stopPolling, token, updateJobStatus]
  );

  const initPolling = useCallback(
    (jobId) => {
      stopPolling(jobId);
      pollMetaRef.current[jobId] = {
        start: Date.now(),
        delay: POLL_BASE_DELAY_MS,
        attempts: 0,
      };
      setJobs((prev) => {
        const job = prev[jobId];
        if (!job) return prev;
        return {
          ...prev,
          [jobId]: {
            ...job,
            retryInfo: { canRetry: false },
          },
        };
      });
      fetchStatus(jobId);
    },
    [fetchStatus, stopPolling]
  );

  const retryPolling = useCallback(
    (jobId) => {
      if (!jobsRef.current[jobId]) return;
      pollMetaRef.current[jobId] = {
        start: Date.now(),
        delay: POLL_BASE_DELAY_MS,
        attempts: 0,
      };
      setJobs((prev) => {
        const job = prev[jobId];
        if (!job) return prev;
        return {
          ...prev,
          [jobId]: {
            ...job,
            retryInfo: { canRetry: false },
            dismissedProgress: false,
          },
        };
      });
      fetchStatus(jobId);
    },
    [fetchStatus]
  );

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    const restored = deserializeJobs(saved);
    if (!restored) return;
    setJobs(restored.entries);
    setJobOrder(restored.order);
    restored.order.forEach((jobId) => {
      const job = restored.entries[jobId];
      if (job && isActiveState(job.status?.state)) {
        initPolling(jobId);
      }
    });
  }, [initPolling]);

  useEffect(
    () => () => {
      Object.keys(pollTimeoutRef.current).forEach((jobId) => {
        clearTimeout(pollTimeoutRef.current[jobId]);
      });
    },
    []
  );

  const addJobEntry = useCallback((jobId, job) => {
    setJobs((prev) => ({ ...prev, [jobId]: job }));
    setJobOrder((prev) =>
      prev.includes(jobId) ? prev : [jobId, ...prev]
    );
  }, []);

  const removeJobEntry = useCallback(
    (jobId) => {
      stopPolling(jobId);
      setJobs((prev) => {
        if (!prev[jobId]) return prev;
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      setJobOrder((prev) => prev.filter((id) => id !== jobId));
      if (activeModalJobId === jobId) {
        closeModals();
      }
    },
    [activeModalJobId, closeModals, stopPolling]
  );

  const startJob = useCallback(
    (jobId, meta = {}) => {
      if (!jobId) return;
      const destinatarios = Number(meta?.destinatarios) || 0;
      const jobData = {
        jobId,
        meta,
        status: buildInitialStatus(destinatarios),
        startedAt: Date.now(),
        completed: false,
        dismissedProgress: false,
        retryInfo: { canRetry: false },
      };
      addJobEntry(jobId, jobData);
      setActiveModalJobId(jobId);
      setActiveModalType("progress");
      pollMetaRef.current[jobId] = {
        start: Date.now(),
        delay: POLL_BASE_DELAY_MS,
        attempts: 0,
      };
      fetchStatus(jobId);
    },
    [addJobEntry, fetchStatus]
  );

  const jobsList = useMemo(
    () => jobOrder.map((id) => jobs[id]).filter(Boolean),
    [jobOrder, jobs]
  );

  const inProgressCount = useMemo(
    () =>
      jobsList.filter((job) => isActiveState(job.status?.state)).length,
    [jobsList]
  );

  const hasFailures = useMemo(
    () =>
      jobsList.some(
        (job) => job.status?.state === "FAILURE" || job.status?.state === "PARTIAL_SUCCESS"
      ),
    [jobsList]
  );

  const currentJob = activeModalJobId ? jobs[activeModalJobId] : null;
  const currentStatus = currentJob?.status || buildInitialStatus();
  const currentProgress = currentStatus.progress || buildInitialStatus().progress;
  const currentRetryInfo = currentJob?.retryInfo || { canRetry: false };

  const contextValue = useMemo(
    () => ({
      startJob,
    }),
    [startJob]
  );

  const renderJobCenter = () => (
    <Drawer
      placement="right"
      size="md"
      isOpen={jobCenterOpen}
      onClose={() => setJobCenterOpen(false)}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Centro de envíos</DrawerHeader>
        <DrawerBody>
          {jobsList.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No hay envíos en seguimiento.
            </Text>
          ) : (
            <Stack spacing={4}>
              {jobsList.map((job) => {
                const state = job.status?.state || "PENDING";
                const stage = job.status?.progress?.stage || "Procesando envío...";
                const percent = job.status?.progress?.percent;
                const total = job.status?.progress?.total || job.meta?.destinatarios || 0;
                const done = job.status?.progress?.done || 0;
                const channel =
                  job.meta?.modo_envio === "whatsapp" ? "WhatsApp" : "Email";
                const badgeColor =
                  state === "SUCCESS"
                    ? "green"
                    : state === "FAILURE"
                    ? "red"
                    : state === "PARTIAL_SUCCESS"
                    ? "orange"
                    : state === "PROGRESS"
                    ? "blue"
                    : "gray";
                return (
                  <Box key={job.jobId} borderWidth="1px" borderRadius="lg" p={4}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontWeight="semibold">
                        #{job.jobId.slice(-6)} • {channel}
                      </Text>
                      <Badge colorScheme={badgeColor}>{state}</Badge>
                    </Flex>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      {stage}
                    </Text>
                    <Progress
                      value={
                        typeof percent === "number"
                          ? Math.min(100, Math.max(0, percent))
                          : undefined
                      }
                      isIndeterminate={
                        typeof percent !== "number" || Number.isNaN(percent)
                      }
                      size="sm"
                      colorScheme="blue"
                      mb={2}
                    />
                    <Text fontSize="xs" color="gray.500">
                      {done}/{total} destinatarios procesados
                    </Text>
                    <HStack mt={4} spacing={2} flexWrap="wrap">
                      <Button
                        size="sm"
                        onClick={() => openProgressModal(job.jobId)}
                        isDisabled={!isActiveState(state)}
                      >
                        Ver progreso
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openResultModal(job.jobId)}
                        isDisabled={!isTerminalState(state)}
                      >
                        Ver resultado
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeJobEntry(job.jobId)}
                      >
                        Cerrar job
                      </Button>
                    </HStack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  return (
    <EnvioInformeJobContext.Provider value={contextValue}>
      {children}

      <EnvioInformeProgressModal
        isOpen={activeModalType === "progress" && Boolean(currentJob)}
        onClose={closeProgressModal}
        progress={currentProgress}
        onRetry={
          currentRetryInfo.canRetry
            ? () => retryPolling(activeModalJobId)
            : undefined
        }
        canRetry={currentRetryInfo.canRetry}
      />

      <EnvioInformeResultModal
        isOpen={activeModalType === "result" && Boolean(currentJob)}
        onClose={closeResultModal}
        payload={currentStatus.result}
        state={currentStatus.state}
      />

      {jobsList.length > 0 && (
        <Portal>
          <Box position="fixed" bottom="24px" left="24px" zIndex={1600}>
            <Tooltip
              label="Abrir centro de envíos"
              placement="right"
              hasArrow
            >
              <Button
                colorScheme="blue"
                onClick={() => setJobCenterOpen(true)}
              >
                📤 Envíos en curso: {inProgressCount}
                {hasFailures && (
                  <Text as="span" ml={2}>
                    ⚠️
                  </Text>
                )}
              </Button>
            </Tooltip>
          </Box>
        </Portal>
      )}

      {renderJobCenter()}
    </EnvioInformeJobContext.Provider>
  );
};
